import { FormEvent, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Field, Input } from '../components/ui/Field';
import { ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';

export function AuthCallbackPage() {
  const { completeCallback, isAuthenticated, resumeSignup } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<Error | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [isRetrying, setIsRetrying] = useState(false);
  const callbackPromiseRef = useRef<Promise<void> | null>(null);

  useEffect(() => {
    let cancelled = false;

    callbackPromiseRef.current ??= completeCallback(window.location.href);
    callbackPromiseRef.current
      .then(() => {
        if (!cancelled) {
          navigate('/dashboard', { replace: true });
        }
      })
      .catch((cause: unknown) => {
        if (!cancelled) {
          setError(cause instanceof Error ? cause : new Error('Sign in failed'));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [completeCallback, navigate]);

  const errorCode = error instanceof ApiError ? error.details.code : undefined;
  const needsDisplayName = errorCode === 'DISPLAY_NAME_REQUIRED';
  const canRetrySetup = errorCode === 'COGNITO_NAME_SYNC_FAILED' || isAuthenticated;

  async function handleRetry(event?: FormEvent) {
    event?.preventDefault();
    if (isRetrying) return;
    setIsRetrying(true);
    setError(null);
    try {
      await resumeSignup(needsDisplayName ? displayName : undefined);
      navigate('/dashboard', { replace: true });
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error('Could not finish account setup'));
      setIsRetrying(false);
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-bone px-4 text-ink">
      <div className="w-full max-w-md rounded-sm bg-white p-6 text-center shadow-soft sm:p-8">
        {error ? (
          <>
            <h1 className="text-lg font-semibold">
              {needsDisplayName ? 'Finish creating your profile' : canRetrySetup ? 'Almost there' : 'Could not sign in'}
            </h1>
            <p className="mt-2 text-sm text-ink/70">{error.message}</p>
            {needsDisplayName ? (
              <form className="mt-5 grid gap-4 text-left" onSubmit={handleRetry}>
                <Field label="Business or display name">
                  <Input
                    value={displayName}
                    onChange={(event) => setDisplayName(event.target.value)}
                    maxLength={100}
                    required
                    autoComplete="organization"
                    autoFocus
                  />
                </Field>
                <Button type="submit" disabled={!displayName.trim() || isRetrying}>
                  {isRetrying ? 'Finishing setup…' : 'Finish setup'}
                </Button>
              </form>
            ) : canRetrySetup ? (
              <Button className="mt-5 w-full" type="button" onClick={() => void handleRetry()} disabled={isRetrying}>
                {isRetrying ? 'Retrying…' : 'Retry account setup'}
              </Button>
            ) : (
              <Link className="mt-5 inline-flex min-h-11 items-center justify-center text-sm font-semibold underline underline-offset-4" to="/">
                Return to Storyly
              </Link>
            )}
          </>
        ) : (
          <>
            <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-ink/20 border-t-ink" />
            <p className="mt-3 text-sm font-medium">Completing sign in</p>
          </>
        )}
      </div>
    </main>
  );
}
